import { useState } from "react";
import styled from "styled-components";
import { IconFullscreen } from "suomifi-icons";
import { useAppState } from "../state/store";
import MuistiinpanoStickyModal from "./MuistiinpanoStickyModal";
import { useGetKayttajaCustomQuery } from "../api/backendApi";
import { skipToken } from "@reduxjs/toolkit/query";

/**
 * Sticky-muistiinpano — yksinkertaistettu vaihtoehto MuistiinpanoPaneelille
 * (työpaja 1.6.2026: testataan yhdelle käyttäjälle ensin).
 *
 * Suunnitteluvalinnat:
 * - Yksi tekstilaatikko per käyttäjä (ei chat-tyylinen lista). Tämä rajaa
 *   "muistiinpanot" virkailijan sisäiseksi muistilapuksi, ei tietosuoja-
 *   pyynnön piiriin kuuluvaksi keskustelutiedoksi.
 * - Ei tallenna-painiketta — autosaves typing-aikana. Analogia: macOS
 *   Stickies / Apple Notes.
 * - Post-it-tyylinen kirkkaampi keltainen tausta visuaalisesti erotellen
 *   sen muusta sisällöstä (warningLight1 + hieman saturaatiota DS:n
 *   warningBase-suuntaan).
 * - Laajenna-ikoni oikeassa yläkulmassa avaa saman tekstin modaalissa
 *   suuremmalla työtilalla (Notion/Linear -pattern).
 */

interface Props {
  kayttajaUid: string;
}

/* Post-it-tyylinen tausta — warningLight1 olisi liian pale, ja warningBase
   liian kirkas. Käytetään välimuotoa hsl(48, 100%, 86%) — kirkkaampi
   post-it-keltainen, joka erottuu sivun harmaasta taustasta.
   DS-tokeneissa ei ole tarkkaa "post-it" -keltaista, joten käytetään
   suoraa HSL-arvoa kuten samaan tarkoitukseen muut prototyypit (esim.
   Apple Notes -teema). Box-shadow antaa lievän nostetun tunteen. */
/* Sisäiset välistykset tiivistetään insetXs (4px) ja insetM (8px):
   otsikon ja Hint:n välille 4px, Hint:n ja Textarean välille 8px.
   Ulkoreunan padding pidetään samana (m = 20px), jotta laatikko
   mätsää muiden sivupalkkikorttien kanssa.
   Kiinteä korkeus 320px sticky-note -tyyliin. Textarea täyttää
   loput tilasta flex:1. */
const Panel = styled.aside`
  background: hsl(48, 100%, 86%);
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  height: 20rem;
  box-shadow: 0 1px 3px hsla(0, 0%, 13%, 0.12);
  position: relative;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Title = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brandBase};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Hint = styled.p`
  margin: ${({ theme }) => theme.spacing.insetXs} 0 0 0;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

/* Laajenna-painike — pieni, ikoni-only, neutraali sticky-taustaa vasten.
   Sijoitettu otsikkorivin oikealle puolelle. */
const ExpandButton = styled.button`
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.xxs};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.depthDark1};
  border-radius: 2px;

  &:hover {
    background: hsla(48, 100%, 50%, 0.15);
    color: ${({ theme }) => theme.colors.brandBase};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 1px;
  }

  & > svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StickyTextarea = styled.textarea`
  border: 0;
  outline: 0;
  background: transparent;
  resize: none;
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.insetM};
  font: ${({ theme }) => theme.typography.bodyText};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
  flex: 1;
  font-family: inherit;
  line-height: 1.45;

  &::placeholder {
    color: ${({ theme }) => theme.colors.depthDark1};
  }
`;

export default function MuistiinpanoSticky({ kayttajaUid }: Props) {
  const { state, dispatch } = useAppState();
  const [modalOpen, setModalOpen] = useState(false);
  const { data: kayttaja } = useGetKayttajaCustomQuery(
    kayttajaUid ?? skipToken,
  );

  const teksti =
    kayttaja?.muistiinpanot?.map((m) => m.teksti).join("\n\n") ?? "";

  function handleChange(uusi: string) {
    dispatch({
      type: "UPDATE_MUISTIINPANO_STICKY",
      kayttajaUid,
      teksti: uusi,
    });
  }

  return (
    <>
      <Panel>
        <TitleRow>
          <Title>Sisäiset muistiinpanot</Title>
          <ExpandButton
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label="Laajenna muistiinpanot"
            title="Laajenna"
          >
            <IconFullscreen aria-hidden />
          </ExpandButton>
        </TitleRow>
        <Hint>Näkyy vain virkailijoille.</Hint>
        <StickyTextarea
          value={teksti}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Kirjoita muistiinpano..."
          rows={4}
          aria-label="Sisäiset muistiinpanot"
        />
      </Panel>

      <MuistiinpanoStickyModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        teksti={teksti}
        onChange={handleChange}
      />
    </>
  );
}
